import { StackScreenProps } from "@react-navigation/stack";
import {
  StockOrderRootParamList,
  ViewStockOrderRootScreenParamList,
} from "../../../../types";
import { useLazyQuery } from "@apollo/client";
import React from "react";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { QUERY_STOCK_ORDER } from "../../utils";
import { PageErrorOccurred } from "../../../../components";
import { PlaceholderLoadingScreen } from "../../../products/screens/PlaceholderLoadingScreen";
import { ResourcePageSkeleton } from "../../../point-of-sale/screens/ResourcPageSkeleton";
import { StockOrder } from "@towersystems/roam-common/lib/generated-types";
import {
  ActiveStockOrderContext,
  useStockOrder,
} from "../../utils/active-stock-order";
import {
  ModalCloseButton,
  createStackHeaderNavigator,
} from "@towersystems/roam-common-native";
import { ViewStockOrderScreen } from "../ViewStockOrderScreen";

export type ViewStockOrderRootScreenProps = StackScreenProps<
  StockOrderRootParamList,
  "ViewStockOrderRootScreen"
>;

export const ViewStockOrderRoot = ({
  stockOrder,
}: ViewStockOrderRootScreenProps & { stockOrder: StockOrder }) => {
  const context = useStockOrder(stockOrder);

  const Drawer =
    createStackHeaderNavigator<ViewStockOrderRootScreenParamList>();

  return (
    <ActiveStockOrderContext.Provider value={context}>
      <Drawer.Navigator
        screenOptions={{
          title: "",
          animationEnabled: true,
          headerLeft: () => <ModalCloseButton type="Back" />,
        }}
      >
        <Drawer.Group>
          <Drawer.Screen
            name="ViewStockOrderScreen"
            component={ViewStockOrderScreen}
          />
        </Drawer.Group>
      </Drawer.Navigator>
    </ActiveStockOrderContext.Provider>
  );
};

export const ViewStockOrderRootScreen = (
  props: ViewStockOrderRootScreenProps
) => {
  const [stockOrder, setStockOrder] = React.useState<StockOrder | undefined>(
    undefined
  );

  const [error, setError] = React.useState<string | undefined>(undefined);
  const transitionEnded = useHasTransitionEnded(props.navigation);

  const id = props.route.params.stockOrderId;
  const [q] = useLazyQuery(QUERY_STOCK_ORDER);

  React.useEffect(() => {
    if (id) {
      q({
        variables: { id },
      })
        .then((res) => {
          if (res.data) {
            setStockOrder(res.data.stockOrder);
          }
        })
        .catch((err) => {
          setError(err.message ?? "An error occurred");
        });
    }
  }, [id, q]);

  if (!stockOrder && error) {
    return (
      <PageErrorOccurred
        errorMessage={error}
        onRetry={() => {
          props.navigation.reset({
            index: 0,
            routes: [{ name: "StockOrderListScreen" }],
          });
        }}
      />
    );
  }

  // Wait for transition to end before rendering the screen
  // otherwise the screen animation will be janky
  if (!stockOrder || false == transitionEnded) {
    return (
      <PlaceholderLoadingScreen
        navigation={props.navigation}
        content={ResourcePageSkeleton}
      />
    );
  }

  return <ViewStockOrderRoot stockOrder={stockOrder} {...props} />;
};
